import { useQuery } from '@apollo/client';
import { FormikProps } from 'formik';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useAuth } from '../../../context/Auth';
import { truncateString } from '../../../helpers/functions';
import { LIST_WALLET_ASSETS } from '../../../http/apollo-queries';
import { Checkbox } from '../../form';
import { Loading } from '../../UI';
import { Title } from '../../NewStrategy/Title';
import { WalletAsset } from '../../NewStrategy/types';
import { FormValues } from '../types';
import { upperCase } from 'lodash';
import { logger } from '../../../helpers/logger';

export const WalletAssets = ({
  formik,
  itemId,
  walletAccount,
}: {
  formik: FormikProps<FormValues>;
  itemId: string;
  walletAccount?: string;
}) => {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [walletAssetsError, setWalletAssetsError] = useState<any>(null);
  const [count, setCount] = useState(0);
  const [noData, setNoData] = useState({
    state: true,
    message: '',
  });

  const {
    data: { listWalletAssets: walletAssets = [] } = {},
    loading,
    refetch,
  } = useQuery<{ listWalletAssets: WalletAsset[] }>(LIST_WALLET_ASSETS, {
    variables: {
      profileId: user?.profile?.id,
      accountId: walletAccount ?? itemId,
    },
    fetchPolicy: 'network-only',
    skip: walletAccount?.length === 0,
    onError: (error) => setWalletAssetsError(error),
  });

  const { selectedWalletAssets } = formik.values;

  const handleSelectedWalletAssets = (asset: WalletAsset) => {
    const newAssets = selectedWalletAssets.some(
      ({ symbol: assetMask }) => assetMask === asset.symbol
    )
      ? selectedWalletAssets.filter((el) => el.symbol !== asset.symbol)
      : [...selectedWalletAssets, asset];

    formik.setValues({ ...formik.values, selectedWalletAssets: newAssets });
  };

  const setIsLoading = (loading: boolean) =>
    formik.setValues({ ...formik.values, loading });

  useEffect(() => {
    setIsLoading(true);
    if (walletAssetsError) {
      if (walletAssetsError?.message === 'ACCOUNT_IN_SYNC') {
        setIsLoading(true);
        setTimeout(() => {
          refetch().then(
            (data) => {
              logger.log('WalletsAssets:data', data);
              setWalletAssetsError(null);
              setIsLoading(false);
            },
            (err) => {
              setCount(count + 1);

              if (count === 6) {
                setWalletAssetsError(null);
                setIsLoading(false);
                setNoData({ state: true, message: 'No data available' });
              } else {
                setWalletAssetsError(err);
              }
            }
          );
        }, 20000);
      } else {
        setIsLoading(false);
        setNoData({ state: true, message: 'An error occurred!' });
      }
    } else {
      setIsLoading(false);
    }
  }, [walletAssetsError]);

  return formik.values.loading || loading ? (
    <div className="flex flex-col gap-5 my-auto">
      <Loading className="h-full m-auto" status />
      <p className="dark:text-white text-center text-grey-50">
        {t('strategies.newStrategy.walletLoading')}
      </p>
    </div>
  ) : (
    <div className="flex flex-col h-full overflow-y-auto">
      <div>
        <p className={'text-base font-semibold p-4 border-t border-grey-10'}>
          {t('strategies.addAsset.subText')}
        </p>
      </div>
      <div className="flex flex-col h-full">
        <div
          className={`
            grid
            grid-cols-6
            items-center
            w-full
            py-4
            bg-white
            border-t
            border-b
            border-grey-10
            dark:bg-black
        `}
        >
          <span className="uppercase col-span-3 mx-3 font-medium text-xs">
            {t('strategies.asset')}
          </span>
          <span className="uppercase font-medium text-xs">
            {t(
              'strategies.configureAsset.form.placeholder.entryPriceInAssetCurrency'
            )}
          </span>
          <span className="uppercase font-medium text-xs">
            {t('strategies.totalValue')}
          </span>
          <span className="uppercase font-medium text-xs">
            {t('strategies.configureAsset.form.placeholder.quantity')}
          </span>
        </div>

        {walletAssets &&
          walletAssets.map(({ symbol, name, categoryId, unit, value }, i) => (
            <div
              key={i}
              className={`
                grid
                grid-cols-6
                items-center
                w-full
                py-4
                bg-grey-10
                dark:bg-black
                border-b
                border-grey-30
                last:border-0
              `}
            >
              <Title
                className="col-span-3"
                description={truncateString(name, 15)}
                logo={null}
                logoRadius="7"
                title={upperCase(name)}
                prefix={
                  <Checkbox
                    className="mx-3"
                    isCheck={selectedWalletAssets.some(
                      ({ symbol: assetMask }) => assetMask === symbol
                    )}
                    shape="square"
                    value={walletAssets[i]}
                    onClick={handleSelectedWalletAssets}
                  />
                }
              />
              <span className={'text-sm truncate'}>{value.toFixed(2)}</span>
              <span className={'text-sm truncate'}>
                {(value * unit).toFixed(2)}
              </span>
              <span className="uppercase text-sm truncate">{`${unit}`}</span>
            </div>
          ))}
        {noData.state && (
          <div className="flex justify-center py-4 font-bold">
            {noData.message}
          </div>
        )}
      </div>
    </div>
  );
};
